import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  ReportTemplateConfig, 
  FleetReportTemplate,
  LOCKED_SECTIONS,
  sanitizeReportTemplate 
} from "@/types/fleet-reports";
import type { Json } from "@/integrations/supabase/types";

const DEFAULT_TEMPLATE_CONFIG: ReportTemplateConfig = {
  logo: { enabled: true, position: 'left' },
  header: { title: '', subtitle: '' },
  sections: [
    { id: 'kpis', enabled: true, order: 1, locked: false },
    { id: 'byVehicle', enabled: true, order: 2, locked: true },
    { id: 'byDriver', enabled: true, order: 3, locked: true },
    { id: 'charts', enabled: true, order: 4, locked: false },
    { id: 'alerts', enabled: true, order: 5, locked: false }
  ],
  columns: {},
  footer: { notes: '', showPagination: true },
  orientation: 'portrait'
};

/**
 * Hook para CRUD de templates de relatório por empresa + tipo
 */
export function useReportTemplates(companyId?: string, reportType?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query para buscar template por empresa + tipo
  const query = useQuery({
    queryKey: ['report-templates', companyId, reportType],
    queryFn: async (): Promise<FleetReportTemplate | null> => {
      if (!companyId || !reportType) return null;

      const { data, error } = await supabase
        .from('fleet_report_templates')
        .select('*')
        .eq('company_id', companyId)
        .eq('report_type', reportType)
        .maybeSingle();

      if (error) {
        console.error('[ReportTemplates] Erro ao buscar template:', error);
        return null;
      }

      if (data) {
        const config = data.template_config as unknown as ReportTemplateConfig;
        return {
          id: data.id,
          company_id: data.company_id,
          report_type: data.report_type,
          template_config: sanitizeReportTemplate(config),
          created_at: data.created_at,
          updated_at: data.updated_at,
          created_by: data.created_by || undefined
        };
      }

      return null;
    },
    enabled: !!companyId && !!reportType
  });

  // Mutation para salvar/atualizar template
  const saveMutation = useMutation({
    mutationFn: async (config: ReportTemplateConfig) => {
      if (!companyId || !reportType) {
        throw new Error('Company ID e Report Type são obrigatórios');
      }

      // Sanitizar para garantir seções travadas
      const sanitizedConfig = sanitizeReportTemplate(config);

      const { data, error } = await supabase
        .from('fleet_report_templates')
        .upsert({
          company_id: companyId,
          report_type: reportType,
          template_config: sanitizedConfig as unknown as Json,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'company_id,report_type'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['report-templates', companyId, reportType] 
      });
      toast({
        title: "Template salvo",
        description: "As configurações do relatório foram salvas com sucesso"
      });
    },
    onError: (error) => {
      console.error('[ReportTemplates] Erro ao salvar:', error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar o template",
        variant: "destructive"
      });
    }
  });

  // Função para resetar para padrão
  const resetToDefault = () => {
    if (!companyId || !reportType) return;
    saveMutation.mutate(DEFAULT_TEMPLATE_CONFIG);
  };

  // Retorna config do template ou padrão
  const templateConfig = query.data?.template_config || DEFAULT_TEMPLATE_CONFIG;

  return {
    template: query.data,
    templateConfig,
    isLoading: query.isLoading,
    saveTemplate: saveMutation.mutate,
    isSaving: saveMutation.isPending,
    resetToDefault,
    defaultConfig: DEFAULT_TEMPLATE_CONFIG
  };
}

/**
 * Hook para listar todos os templates de uma empresa
 */
export function useAllReportTemplates(companyId?: string) {
  return useQuery({
    queryKey: ['report-templates-all', companyId],
    queryFn: async (): Promise<FleetReportTemplate[]> => {
      if (!companyId) return [];

      const { data, error } = await supabase
        .from('fleet_report_templates')
        .select('*')
        .eq('company_id', companyId);

      if (error) {
        console.error('[ReportTemplates] Erro ao listar templates:', error);
        return [];
      }

      return (data || []).map(item => {
        const config = item.template_config as unknown as ReportTemplateConfig;
        return {
          id: item.id,
          company_id: item.company_id,
          report_type: item.report_type,
          template_config: sanitizeReportTemplate(config),
          created_at: item.created_at,
          updated_at: item.updated_at,
          created_by: item.created_by || undefined
        };
      });
    },
    enabled: !!companyId
  });
}
